/*
 * /chat/[slug] (page)
 */
import { createFileRoute } from "@tanstack/react-router"
import type { ChatMessage } from "@psychology/db/client"
import { AIChatRoomWindow } from "@psychology/ai/client"
import { Container, Divider, IconButton, Page } from "@psychology/react-ui"
import { AppPlatform, capitalizeFirstLetter } from "@psychology/utils"
import type { UIMessage } from "ai"
import { ArrowLeft, Brain } from "lucide-react"
import { useState } from "react"
import { HeaderBanner } from "../../components/header-banner"
import { trpc } from "../../trpc"
import { authPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/chat/$chatRoomId")({
	component: ChatPage,
	loader: authPageLoader,
})

function ChatPage() {
	const { user, token } = Route.useLoaderData()
	const { chatRoomId } = Route.useParams()
	const { purpose = "psychology_unknown" } = Route.useSearch<any>()
	const [isUpdatingContext, setIsUpdatingContext] = useState(false)

	// const processContextMutation =
	// 	trpc.chatRoomContext.process.useMutation({
	// 		onSuccess: () => {
	// 			setIsUpdatingContext(false)
	// 			utils.psychologicalContext.getByFilter.invalidate()
	// 		},
	// 		onError: () => {
	// 			setIsUpdatingContext(false)
	// 		},
	// 	})

	async function updateContext() {
		setIsUpdatingContext(true)
		// await processContextMutation.mutateAsync({ chatRoomId })
	}

	const deleteChatMessageMutation = trpc.chatMessage.delete.useMutation()

	async function deleteChatMessage(chatMessageId: string) {
		const result = await deleteChatMessageMutation.mutateAsync({
			id: chatMessageId,
		})
		return Boolean(result)
	}

	const chatRoom = trpc.chatRoom.getByFilter.useQuery({
		where: { id: chatRoomId, userId: user.id },
	})
	console.log("ChatRoom", { chatRoom })

	const chatMessages = trpc.chatMessage.getAllByFilter.useQuery({
		pagination: { limit: 50, offset: 0 },
		sort: { column: "createdAt", direction: "asc" },
		where: { chatRoomId },
	})

	function parseChatMessagesToAIMessages(messages: ChatMessage[]): UIMessage[] {
		return messages.map(({ privateNotes, content, ...message }: any) => ({
			role: message.aiModelId ? "assistant" : "user",
			privateNotes:
				((user?.user_metadata?.[AppPlatform] as any)?.isAdmin &&
					privateNotes) ||
				undefined,
			parts: [{ type: "text", text: content }],
			...message,
		})) as UIMessage[]
	}

	if (chatRoom.isLoading || chatMessages.isLoading) {
		return (
			<Page centerVertical={true}>
				<Container width={"xl"} padding={"lg"}>
					<div className={"text-center"}>{"Loading..."}</div>
				</Container>
			</Page>
		)
	}

	const initialMessages = parseChatMessagesToAIMessages(chatMessages.data || [])

	const apiRoute = `${import.meta.env.VITE_API_DOMAIN || ""}/api/v1/chat`

	return (
		<Page scrolling={false}>
			<HeaderBanner
				title={capitalizeFirstLetter(chatRoom.data?.title || "New chat")}
				left={{ to: "/chat", label: "Back", Icon: <ArrowLeft size={20} /> }}
				right={{
					Component: (
						<IconButton
							Icon={Brain}
							variant={"secondary"}
							size={"sm"}
							isLoading={isUpdatingContext}
							onClick={updateContext}
						>
							{"Update Context"}
						</IconButton>
					),
				}}
			/>
			<Divider />
			<AIChatRoomWindow
				className={"flex-1 overflow-auto"}
				userId={user.id}
				authToken={token}
				chatRoomId={chatRoomId}
				apiRoute={apiRoute}
				deleteChatMessage={deleteChatMessage}
				initialMessages={initialMessages}
				roomPurpose={purpose}
			/>
		</Page>
	)
}
